import { supabase } from '@/integrations/supabase/client';

/**
 * REK-1925: the single browser-side entry point for persisting taxonomy
 * answers into `user_knowledge` (the `survey_qm_{question_id}`
 * namespace).
 *
 * Previously four surfaces (profile wizard, sign-up identity, one-click
 * sign-up, campaign partial-save) upserted `survey_qm_*` rows DIRECTLY via
 * PostgREST. That let any authenticated user fabricate campaign-completion
 * rows and forge recurring payouts. A companion DB trigger now rejects
 * anon/authenticated writes to `survey_qm_*`, so those surfaces route through
 * the `persist-survey-answers` edge function (service role) instead. The row
 * shape and semantics are unchanged — only the writer changed.
 *
 * Rewards are NOT minted here; the endpoint only persists. Point rewards stay
 * owned by their own server paths (complete-profile-step, submit-survey-answer,
 * submit-campaign-response).
 */

export type SurveyAnswerSource =
  | 'lemonade_wizard'
  | 'onboarding'
  | 'unified_campaign'
  | 'personalized_survey'
  /** REK-2186: SSO provider enrichment — edge allowlists + insert-only. */
  | 'sso';

export interface SurveyAnswerInput {
  questionMappingId: string;
  value: string | string[];
  source?: SurveyAnswerSource;
}

export interface PersistSurveyAnswersResult {
  error: string | null;
  persisted: number;
}

/**
 * Best-effort extraction of the server-provided error message from a
 * supabase-js function error. On a non-2xx response `error.message` is generic
 * and the real body lives on `error.context` (a `Response`). We clone + parse
 * it for the `{ error }` field; on any failure we fall back to the generic
 * message.
 */
async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback =
    (error as { message?: string })?.message ?? 'Failed to persist answers';
  const ctx = (error as { context?: unknown })?.context;
  if (ctx && typeof (ctx as Response).clone === 'function') {
    try {
      const body = await (ctx as Response).clone().json();
      if (body && typeof body.error === 'string') return body.error;
    } catch {
      // Non-JSON / already-consumed body — keep the fallback.
    }
  }
  return fallback;
}

export interface PersistSurveyAnswersOptions {
  /** Required for multi-answer campaign partial-save (REK-1977 entitlement). */
  campaignId?: string;
}

/**
 * Persist one or more taxonomy answers via the `persist-survey-answers` edge
 * function. Non-throwing: returns `{ error }` so callers can decide whether to
 * surface a retry (the profile wizard rethrows; sign-up flows log-and-continue).
 */
export async function persistSurveyAnswers(
  answers: SurveyAnswerInput[],
  options?: PersistSurveyAnswersOptions,
): Promise<PersistSurveyAnswersResult> {
  if (!answers || answers.length === 0) {
    return { error: null, persisted: 0 };
  }

  const body: { answers: SurveyAnswerInput[]; campaignId?: string } = { answers };
  if (options?.campaignId) {
    body.campaignId = options.campaignId;
  }

  const { data, error } = await supabase.functions.invoke('persist-survey-answers', {
    body,
  });

  if (error) {
    // supabase-js reports a non-2xx edge-function response as a
    // FunctionsHttpError whose `message` is generic ("Edge Function returned a
    // non-2xx status code") and carries the underlying Response on `.context`.
    // Surface the server's real message (e.g. rate-limit / validation) when we
    // can parse it, so logs/callers see the actual cause.
    const message = await extractFunctionErrorMessage(error);
    return { error: message, persisted: 0 };
  }
  if (data && data.success === false) {
    return { error: (data.error as string) ?? 'Failed to persist answers', persisted: 0 };
  }

  const persisted = data && typeof data.persisted === 'number' ? data.persisted : answers.length;
  return { error: null, persisted };
}
